import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { useToast } from '@/shared/hooks/useToast';
import { useSaveFormAsTemplate } from '../../hooks/useSaveFormAsTemplate';

interface SaveAsTemplateDialogProps {
  formId: string;
  formTitle: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function SaveAsTemplateDialog({
  formId,
  formTitle,
  open,
  onOpenChange,
}: SaveAsTemplateDialogProps) {
  const { t } = useTranslation('forms');
  const { toast } = useToast();
  const saveAsTemplate = useSaveFormAsTemplate(formId);
  const [title, setTitle] = useState('');

  async function handleSave() {
    try {
      await saveAsTemplate.mutateAsync(title.trim() || undefined);
      toast.success({ title: t('templates.saveAsTemplate.toast') });
      setTitle('');
      onOpenChange(false);
    } catch {
      // error handled by global handler in bootstrap.ts
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="forms-save-as-template-dialog">
        <DialogHeader>
          <DialogTitle>{t('templates.saveAsTemplate.dialogTitle')}</DialogTitle>
        </DialogHeader>

        <p className="text-sm text-muted-foreground">
          {t('templates.saveAsTemplate.dialogDescription')}
        </p>

        <div className="space-y-1.5">
          <Label htmlFor="forms-save-as-template-title-input">
            {t('templates.saveAsTemplate.titleLabel')}
          </Label>
          <Input
            id="forms-save-as-template-title-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={`${formTitle} (plantilla)`}
            data-testid="forms-save-as-template-title-input"
          />
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saveAsTemplate.isPending}
            data-testid="forms-save-as-template-cancel-button"
          >
            {t('share.actions.cancel')}
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={saveAsTemplate.isPending}
            data-testid="forms-save-as-template-submit-button"
          >
            {t('templates.saveAsTemplate.submitButton')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
